import { AuthStore, Channel } from 'frontendcommon-3'
import URLS from './urls'
import axios from 'axios'
import { AbstractAuth } from '@/dashboards/common'
import { TAG_ENV_CODE } from '@/config/urls'
import * as Sentry from '@sentry/vue'
import customerUrls from './../farmapremium-clientes/urls'
import nexteraUrl from './../nextera/urls'

const ADMIN_USER_TYPE = 'adm1n'
const COOP_USER_TYPE = 'coop'
const PHARMACY_USER_TYPE = 'store'
const CUSTOMER_USER_TYPE = 'customer'
const ALLOWED_USER_TYPE = [ADMIN_USER_TYPE, COOP_USER_TYPE]

const sentryEnv = process.env.VUE_APP_SENTRY_ENV
export default class Auth extends AbstractAuth {
  onTokenUnauthorized () {
    this.redirectLogout()
  }

  onForbiddenRequest () {
    this.redirectLogout()
  }

  redirectLogin () {
    const event = new CustomEvent('hide-content')
    window.dispatchEvent(event)
    window.location.assign(URLS.LOGIN_URL)
  }

  redirectLogout (redirectTo = false) {
    const event = new CustomEvent('hide-content')
    window.dispatchEvent(event)
    let logoutUrl = URLS.LOGOUT_URL
    if (redirectTo) logoutUrl += '?redirectTo=' + encodeURI(redirectTo)
    window.location.assign(logoutUrl)
    // console.log(logoutUrl)
  }

  redirectDashboard () {
    const event = new CustomEvent('hide-content')
    window.dispatchEvent(event)
    window.location.assign(URLS.APPS_DASHBOARDS_URL)
  }

  reloadHome () {
    window.location.assign('/')
  }

  reloadPage () {
    window.location.reload()
  }

  async fetchToken () {
    try {
      const { data } = await axios.create().get(URLS.TOKEN_URL, { withCredentials: true })
      return data
    } catch {
      return {}
    }
  }

  async refreshToken (tokens, tokenType) {
    try {
      // only in farmapremium dashboards
      const newTokenData = await this.fetchToken()
      if (tokens.access_token !== newTokenData.token) {
        this.reloadHome()
        return
      }

      const { data } = await axios.create().put(URLS.REFRESH_TOKEN_URL, tokens, { withCredentials: true })
      const newTokens = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires: (Date.now() / 1000) + (data.expires_in)
      }
      this.scheduleRefreshToken(newTokens, tokenType)
      Channel.emitTokenRefreshEnd({ tokenType, tokens: newTokens })
      return { tokenType, tokens: newTokens }
    } catch (error) {
      this.redirectLogout()
    }
  }

  async scheduleRefreshToken (tokens, tokenType) {
    const expTime = tokens.expires * 1000
    if (!expTime) return
    const now = Date.now()
    const refreshTime = expTime - now
    if (refreshTime <= 0) {
      const refreshed = await this.refreshToken(tokens, tokenType)
      return refreshed.tokens
    } else {
      setTimeout(() => {
        this.refreshToken(tokens, tokenType)
      }, refreshTime)
      return tokens
    }
  }

  async fetchShellConfig (token) {
    try {
      const { data } = await axios.create().get(URLS.SHELL_CONFIG_URL, {
        headers: {
          Authorization: this.getAuthHeader(token)
        },
        withCredentials: true
      })
      const mergedConfig = await this.mergeLocalConfig(data)
      return this.resolveActiveItem(mergedConfig)
    } catch (error) {
      return { micros: [], sidebar: [] }
    }
  }

  async setRefreshToken (tokens) {
    const refreshedTokens = {
      ...tokens
    }
    if (tokens.farmapremium.access_token) {
      refreshedTokens.farmapremium = await this.scheduleRefreshToken(tokens.farmapremium, 'farmapremium')
    }
    return refreshedTokens
  }

  async getAuthData () {
    try {
      // get session data
      const tokenData = await this.fetchToken()
      if (!tokenData.token) return this.manageTokenError()
      let tokens = {
        farmapremium: {
          access_token: tokenData.access_token || tokenData.token,
          refresh_token: tokenData.refresh_token,
          expires: tokenData.token_exp
        },
        farmacloud: {
          jti: tokenData.farmacloud_jti
        }
      }
      tokens = await this.setRefreshToken(tokens, 'farmapremium')
      const { micros, sidebar, user } = await this.fetchShellConfig(tokenData.token)
      if (!ALLOWED_USER_TYPE.includes(user.role)) {
        this.redirectForbiddenUserType(user.role)
        throw new TypeError('invalid-user-type')
      }
      // create a common data structure
      const permissions = {}
      micros.forEach(element => {
        permissions[element.id] = true
      })
      const userData = {
        ...user,
        language: 'es',
        is_admin: user.role === ADMIN_USER_TYPE,
        permissions
      }
      const authData = {
        isLogged: true,
        tokens,
        user: userData,
        micros,
        sidebar
      }
      // store only userData in localstorage
      AuthStore.storeUserData(userData)

      this.initExternalTools(authData)
      return authData
    } catch (error) {
      if (error instanceof TypeError && error.message === 'invalid-user-type') {
        throw error
      } else {
        return this.manageTokenError()
      }
    }
  }

  initExternalTools (authData) {
    const sub = AuthStore.decodeTokenSub(authData.tokens.farmapremium.access_token)
    const farmapremiumUserId = sub.user_id
    const farmapremiumGroupId = sub.group_id

    this.initUsetifull(farmapremiumUserId, farmapremiumGroupId, authData)
    this.initSentry(farmapremiumUserId, farmapremiumGroupId, authData)
  }

  initUsetifull (farmapremiumUserId, farmapremiumGroupId, authData) {
    const firstName = AuthStore.getFirstname()
    const userId = `${farmapremiumUserId}-${TAG_ENV_CODE}`
    const segment = ''
    const role = (AuthStore.getIsAdmin()) ? 'adm1n' : 'pharmacy'
    const groudId = farmapremiumGroupId
    const farmacloudExportedData = authData.user.farmacloudExportData
    const showTourFarmacloudLogin = authData.user.showTourFarmacloudLogin
    const showTourNexteraRedirect = authData.user.showTourNexteraRedirect
    window.initUsetiful({ userId, segment, role, firstName, groudId, farmacloudExportedData, showTourFarmacloudLogin, showTourNexteraRedirect })
  }

  initSentry (farmapremiumGroupId) {
    if (sentryEnv) {
      Sentry.setUser({ id: farmapremiumGroupId, username: AuthStore.getFirstname() })
    }
  }

  redirectForbiddenUserType (userType) {
    if (userType === CUSTOMER_USER_TYPE) {
      const event = new CustomEvent('hide-content')
      window.dispatchEvent(event)
      window.location.assign(customerUrls.CUSTOMER_BASE_URL)
    }
    if (userType === PHARMACY_USER_TYPE) {
      const event = new CustomEvent('hide-content')
      window.dispatchEvent(event)
      window.location.assign(nexteraUrl.NEXTERA_BASE_URL)
    }
    throw new TypeError('invalid-user-type')
  }
}
